import { useQuery } from '@tanstack/react-query';
import sportsApi from '@app/utils/sportsApi';
import { CommonKey, SearchParams } from '@app/types/api';
import {
    DemographicFilters,
    EMPTY_DEMOGRAPHIC_FILTERS,
    toAudienceScopes,
} from '@app/constants/demographics';
import { BroadcastDemographicsResponseRow } from './demographicsUtils';

// Unlike /broadcasts/viewership, this endpoint takes the Broadcast Exposure
// query flat in the body — there is no `domainObject` envelope and no
// pagination. Unknown fields are dropped silently, so the demographic
// restrictions have to use the exact field names `ageBrackets` and
// `audienceScopes`; both read an empty array as "all values".
const fetchBroadcastDemographics = async (
    clientId: number,
    searchParams: SearchParams,
    demographicFilters: DemographicFilters,
): Promise<BroadcastDemographicsResponseRow[]> =>
    await sportsApi
        .url(`/clients/${clientId}/broadcasts/audience`)
        .post({
            ...searchParams,
            ageBrackets: demographicFilters.ageBrackets ?? [],
            audienceScopes: toAudienceScopes(demographicFilters.genders),
        })
        .json();

const useBroadcastDemographics = (
    clientId: number,
    searchParams: SearchParams,
    demographicFilters: DemographicFilters = EMPTY_DEMOGRAPHIC_FILTERS,
    options = {},
) =>
    useQuery({
        queryKey: [
            CommonKey.BroadcastDemographics,
            clientId,
            searchParams,
            demographicFilters,
        ],
        queryFn: (): Promise<BroadcastDemographicsResponseRow[]> =>
            fetchBroadcastDemographics(
                clientId,
                searchParams,
                demographicFilters,
            ),
        enabled: Boolean(clientId),
        // The aggregate only changes when the user applies new filters, which
        // changes the key above. react-query's defaults (staleTime 0 plus
        // refetch on window focus, reconnect and remount) would otherwise
        // re-request it on every tab refocus or remount and flash the table
        // into its loading state. Same choice as useBroadcastsViewership.
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        ...options,
    });

export default useBroadcastDemographics;
